import { Injectable, signal, WritableSignal, computed, effect } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';

/**
 * Advanced State Management Service
 * Provides centralized state management with signals and observables
 */
@Injectable({ providedIn: 'root' })
export class StateManagementService {
  // Global application state
  private readonly _loading: WritableSignal<boolean> = signal(false);
  private readonly _error: WritableSignal<string | null> = signal(null);
  private readonly _success: WritableSignal<string | null> = signal(null);
  private readonly _currentTenant: WritableSignal<string | null> = signal(null);
  private readonly _sidebarOpen: WritableSignal<boolean> = signal(true);
  private readonly _theme: WritableSignal<'light' | 'dark'> = signal('light');
  
  // Observables for reactive patterns
  private readonly _loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly _errorSubject = new Subject<string | null>();
  private readonly _successSubject = new Subject<string | null>();
  
  // Computed signals
  readonly isLoading = computed(() => this._loading());
  readonly error = computed(() => this._error());
  readonly success = computed(() => this._success());
  readonly currentTenant = computed(() => this._currentTenant());
  readonly sidebarOpen = computed(() => this._sidebarOpen());
  readonly theme = computed(() => this._theme());
  
  // Observables
  readonly loading$ = this._loadingSubject.asObservable();
  readonly error$ = this._errorSubject.asObservable();
  readonly success$ = this._successSubject.asObservable();

  constructor() {
    // Effects for side effects
    effect(() => {
      const loading = this._loading();
      this._loadingSubject.next(loading);
    });

    effect(() => {
      const error = this._error();
      this._errorSubject.next(error);
    });

    effect(() => {
      const success = this._success();
      this._successSubject.next(success);
    });
  }

  // Loading state
  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  // Error state
  setError(error: string | null): void {
    this._error.set(error);
    if (error) {
      setTimeout(() => this._error.set(null), 5000); // Auto-clear after 5s
    }
  }

  // Success state
  setSuccess(message: string | null): void {
    this._success.set(message);
    if (message) {
      setTimeout(() => this._success.set(null), 3000); // Auto-clear after 3s
    }
  }

  // Tenant state
  setTenant(tenantId: string | null): void {
    this._currentTenant.set(tenantId);
    if (tenantId) {
      localStorage.setItem('current-tenant', tenantId);
    } else {
      localStorage.removeItem('current-tenant');
    }
  }

  // Sidebar state
  toggleSidebar(): void {
    this._sidebarOpen.update(open => !open);
  }

  setSidebarOpen(open: boolean): void {
    this._sidebarOpen.set(open);
  }

  // Theme state
  toggleTheme(): void {
    this._theme.update(theme => theme === 'light' ? 'dark' : 'light');
    this.updateThemeClass();
  }

  setTheme(theme: 'light' | 'dark'): void {
    this._theme.set(theme);
    this.updateThemeClass();
  }

  private updateThemeClass(): void {
    const theme = this._theme();
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }

  // Initialize from localStorage
  initialize(): void {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      this.setTheme(savedTheme);
    }

    const savedTenant = localStorage.getItem('current-tenant');
    if (savedTenant) {
      this._currentTenant.set(savedTenant);
    }

    const sidebarState = localStorage.getItem('sidebar-open');
    if (sidebarState !== null) {
      this._sidebarOpen.set(sidebarState === 'true');
    }
  }

  // Clear all state
  clear(): void {
    this._loading.set(false);
    this._error.set(null);
    this._success.set(null);
  }

  // Reset to initial state
  reset(): void {
    this.clear();
    this._currentTenant.set(null);
    this._sidebarOpen.set(true);
    this._theme.set('light');
  }
}

